import React from "react";
import { useTranslation } from "react-i18next";

import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";

import Input from "~/shared/ui/Fields/Input";
import Button from "~/shared/ui/Fields/Button";
import Edit from "~/components/Icons/icon-edit-2";
import Delete from "~/components/Icons/delete-cross";
import Download from "~/components/Icons/icon-download";
import IconPng from "~/components/Icons/mimeType/icon-png";
import IconJpeg from "~/components/Icons/mimeType/icon-jpeg";
import IconZip from "~/components/Icons/mimeType/icon-zip";
import IconBmp from "~/components/Icons/mimeType/icon-bmp";
import IconCsv from "~/components/Icons/mimeType/icon-csv";
import IconDoc from "~/components/Icons/mimeType/icon-doc";
import IconGif from "~/components/Icons/mimeType/icon-gif";
import IconGzip from "~/components/Icons/mimeType/icon-gzip";
import IconPpt from "~/components/Icons/mimeType/icon-ppt";
import IconRar from "~/components/Icons/mimeType/icon-rar";
import IconSvg from "~/components/Icons/mimeType/icon-svg";
import IconTxt from "~/components/Icons/mimeType/icon-txt";
import IconWebp from "~/components/Icons/mimeType/icon-webp";
import IconWbmp from "~/components/Icons/mimeType/icon-wbmp";
import IconTiff from "~/components/Icons/mimeType/icon-tiff";
import IconXls from "~/components/Icons/mimeType/icon-xls";
import Icon7zip from "~/components/Icons/mimeType/icon-7zip";
import IconPdf from "~/components/Icons/mimeType/icon-pdf";
import IconCheck from "~/components/Icons/icon-check-mark";
import IconCross from "~/components/Icons/icon-cross";
import IconUnknow from "~/components/Icons/mimeType/icon-unknow-file";

import s from "./TableRow.less";
import clsx from "clsx";
import { usePermissions } from "~/shared/context/PermissionContext";

interface IProps {
  options: string[];
  handleSubmit?;
  control?;
  onSubmit?;
  item;
  btnDel?: boolean;
}

const PKDTableRow: React.FC<IProps> = ({
  options = [],
  control,
  handleSubmit,
  onSubmit,
  btnDel = false,
  item = {},
}) => {
  const { t } = useTranslation();
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const setData = useSetLocationData();
  const { openModal, currentId } = useGetLocationData();
  const openDelModal = () => {
    setData("openModal", "Sure", "currentId", item.id);
  };

  const mimeType = item.mimeType;
  const re = /(?:\.([^.]+))?$/;
  const point = re.exec(item.link)[1];

  let icon = {};
  switch (mimeType) {
    case "image/png":
      icon = <IconPng />;
      break;
    case "image/jpeg":
      icon = <IconJpeg />;
      break;
    case "image/pjpeg":
      icon = <IconJpeg />;
      break;
    case "image/bmp":
      icon = <IconBmp />;
      break;
    case "image/x-windows-bmp":
      icon = <IconBmp />;
      break;
    case "image/csv":
      icon = <IconCsv />;
      break;
    case "image/webp":
      icon = <IconWebp />;
      break;
    case "image/vnd.wap.wbmp":
      icon = <IconWbmp />;
      break;
    case "image/gif":
      icon = <IconGif />;
      break;
    case "application/pdf":
      icon = <IconPdf />;
      break;
    case "image/svg+xml":
      icon = <IconSvg />;
      break;
    case "image/tiff":
      icon = <IconTiff />;
      break;
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      icon = <IconDoc />;
      break;
    case "application/msword":
      icon = <IconDoc />;
      break;
    case "application/vnd.ms-excel":
      icon = <IconXls />;
      break;
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      icon = <IconXls />;
      break;
    case "application/zip":
      icon = <IconZip />;
      break;
    case "application/x-zip-compressed":
      icon = <IconZip />;
      break;
    case "application/gzip":
      icon = <IconGzip />;
      break;
    case "application/x-rar-compressed":
      icon = <IconRar />;
      break;
    case "application/vnd.rar":
      icon = <IconRar />;
      break;
    case "application/x-rar":
      icon = <IconRar />;
      break;
    case "application/vnd.ms-powerpoint":
      icon = <IconPpt />;
      break;
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      icon = <IconPpt />;
      break;
    case "application/x-7z-compressed":
      icon = <Icon7zip />;
      break;
    case "text/plain":
      icon = <IconTxt />;
      break;
    case "application/octet-stream":
      if (point == "rar") {
        icon = <IconRar />;
      } else if (point == "wbmp") {
        icon = <IconWbmp />;
      }
      break;
    default:
      icon = <IconUnknow />;
      break;
  }

  const onButtonClick = () => {
    setData("openModal", "edit", "currentId", item.id);
  };

  const onCrossClick = () => {
    setData("openModal", "", "currentId", "");
  };

  return (
    <>
      <tr className={s.row_tr}>
        {options?.map((optionName) => (
          <td key={optionName} data-option_name={optionName}>
            <div>
              <span className={s.cont}>
                {optionName === "name" ? (
                  <>
                    <span onDoubleClick={onButtonClick} className={s.mimeType}>
                      {icon}
                    </span>
                    {openModal === "edit" &&
                    currentId === item.id &&
                    hasPermission("pkd.editFile") ? (
                      <>
                        <form
                          className={s.form}
                          onSubmit={handleSubmit(onSubmit)}
                        >
                          <Input
                            name={"name"}
                            control={control}
                            maxLength={255}
                            defaultValue={item[optionName]}
                          />
                          <Button
                            customClassName={s.save_changes}
                            txtBtn={""}
                            onlyIcon
                            isForm
                          >
                            <IconCheck />
                          </Button>
                        </form>
                        <Button
                          customClassName={s.cross_changes}
                          txtBtn={""}
                          onlyIcon
                          isForm
                          onClick={onCrossClick}
                        >
                          <IconCross />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span
                          className={clsx({
                            [s.nameRow]: optionName === "name",
                          })}
                        >
                          {item[optionName]}
                        </span>
                        {hasPermission("pkd.editFile") && (
                          <Button
                            customClassName={s.edit_changes}
                            txtBtn={""}
                            onlyIcon
                            onClick={onButtonClick}
                          >
                            <Edit />
                          </Button>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  item[optionName]
                )}
              </span>
            </div>
          </td>
        ))}

        <td className={s.with_btn}>
          <div className={s.cont}>
            <a href={item.link} download>
              <Button small txtBtn={t("pkd.download")}>
                <Download />
              </Button>
            </a>
          </div>
          {btnDel && (
            <div className={s.cont}>
              <Button
                small
                txtBtn={t("ui.btn_del")}
                empty
                onClick={openDelModal}
              >
                <Delete />
              </Button>
            </div>
          )}
        </td>
      </tr>
    </>
  );
};
export default PKDTableRow;
